import React, { useEffect, useMemo, useState } from 'react';

const PAGE_SIZE = 12;

const compact = (value, digits = 1) => new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: digits,
}).format(value);

const formatPower = (value) => value >= 1000
    ? `${(value / 1000).toFixed(value >= 10000 ? 1 : 2)} GW`
    : `${Math.round(value)} MW`;

const formatCompute = (value) => `${compact(value, 1)} H100-eq`;

const formatCost = (value) => `$${value.toFixed(value >= 10 ? 1 : 2)}B`;
const formatOptionalPower = (value) => Number.isFinite(value) ? formatPower(value) : '—';
const formatOptionalCompute = (value) => Number.isFinite(value) ? formatCompute(value) : '—';
const formatBuildings = (value, isZh) => Number.isFinite(value)
    ? `${value} ${isZh ? '栋已投运' : 'operational buildings'}`
    : '—';

const getObservedTimeline = (project, asOf) => project.timeline.filter((point) => point.date <= asOf);
const getLatestObserved = (project, asOf) => getObservedTimeline(project, asOf).at(-1) || null;
const getNextProjection = (project, asOf) => project.timeline.find((point) => point.date > asOf) || null;

const confidenceLabel = (confidence, isZh) => {
    if (confidence === 'likely') return isZh ? '较可能' : 'Likely';
    if (confidence === 'speculative') return isZh ? '推测' : 'Speculative';
    return '';
};

const StatusMarkdown = ({ children = '' }) => {
    const nodes = [];
    const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
    let cursor = 0;
    let match = linkPattern.exec(children);

    while (match) {
        if (match.index > cursor) nodes.push(children.slice(cursor, match.index));
        nodes.push(<a href={match[2]} target="_blank" rel="noreferrer" key={`${match.index}-${match[2]}`}>{match[1]}</a>);
        cursor = match.index + match[0].length;
        match = linkPattern.exec(children);
    }
    if (cursor < children.length) nodes.push(children.slice(cursor));

    return <p>{nodes}</p>;
};

const ProjectDetail = ({ project, asOf, isZh }) => {
    const latest = getLatestObserved(project, asOf);
    const nextProjection = getNextProjection(project, asOf);
    const users = project.users.length ? project.users : [{ name: isZh ? '未知' : 'Unknown', confidence: 'unknown' }];
    const chips = project.currentChipTypes.length ? project.currentChipTypes : project.allChipTypes;

    return (
        <div className="ai-dev-dc-detail">
            <div className="ai-dev-dc-detail__facts">
                <div><span>{isZh ? '业主' : 'Owner'}</span><strong>{project.owner}</strong></div>
                <div>
                    <span>{isZh ? '主要用户' : 'Users'}</span>
                    <strong>{users.map((user, index) => (
                        <React.Fragment key={`${user.name}-${index}`}>
                            {index ? ', ' : ''}{user.name}
                            {confidenceLabel(user.confidence, isZh) ? <small> ({confidenceLabel(user.confidence, isZh)})</small> : null}
                        </React.Fragment>
                    ))}</strong>
                </div>
                <div><span>{isZh ? '当前芯片' : 'Current chips'}</span><strong>{chips.join(', ') || '—'}</strong></div>
                <div><span>{isZh ? '当前资本成本估算' : 'Current estimated capital cost'}</span><strong>{formatCost(project.currentCapitalCostB)}</strong></div>
                <div className="ai-dev-dc-detail__address"><span>{isZh ? '位置' : 'Location'}</span><strong>{project.address || project.country}</strong></div>
            </div>

            <div className="ai-dev-dc-detail__summary">
                <div>
                    <span>{isZh ? '最近观察' : 'Latest observation'}</span>
                    <strong>{latest?.date || '—'}</strong>
                    <small>{latest ? `${formatOptionalPower(latest.itPowerMw)} · ${formatBuildings(latest.buildingsOperational, isZh)}` : '—'}</small>
                </div>
                <div className={nextProjection ? 'has-projection' : ''}>
                    <span>{isZh ? '下一个 Epoch 估算节点' : 'Next Epoch estimate'}</span>
                    <strong>{nextProjection?.date || (isZh ? '暂无' : 'None')}</strong>
                    <small>{nextProjection ? `${formatOptionalPower(nextProjection.itPowerMw)} · ${formatOptionalCompute(nextProjection.h100Equivalent)}` : (isZh ? '数据中无未来节点' : 'No future point in the dataset')}</small>
                </div>
                <a href={`https://epoch.ai/data/ai-data-centers/directory/${project.slug}`} target="_blank" rel="noreferrer">
                    {isZh ? '打开 Epoch 站点详情 ↗' : 'Open Epoch site detail ↗'}
                </a>
            </div>

            <div className="ai-dev-dc-timeline" aria-label={isZh ? `${project.name} 建设时间线` : `${project.name} buildout timeline`}>
                {project.timeline.map((point) => {
                    const projected = point.date > asOf;
                    return (
                        <article className={`ai-dev-dc-timeline__event ${projected ? 'is-projected' : ''}`.trim()} key={`${project.name}-${point.date}`}>
                            <div className="ai-dev-dc-timeline__date">
                                <time>{point.date}</time>
                                <span>{projected ? (isZh ? '未来估算' : 'Projection') : (isZh ? '已观察' : 'Observed')}</span>
                            </div>
                            <div className="ai-dev-dc-timeline__metrics">
                                <strong>{formatOptionalPower(point.itPowerMw)}</strong>
                                <span>{formatOptionalCompute(point.h100Equivalent)}</span>
                                <span>{formatBuildings(point.buildingsOperational, isZh)}</span>
                            </div>
                            <div className="ai-dev-dc-timeline__copy"><StatusMarkdown>{point.status}</StatusMarkdown></div>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

const DataCenterBuildoutExplorer = ({ dataset, isZh = false }) => {
    const projects = useMemo(() => dataset?.projects || [], [dataset?.projects]);
    const asOf = dataset?.asOf || '9999-12-31';
    const [query, setQuery] = useState('');
    const [owner, setOwner] = useState('all');
    const [phase, setPhase] = useState('all');
    const [sort, setSort] = useState('compute');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [expandedProject, setExpandedProject] = useState(null);
    const owners = useMemo(() => [...new Set(projects.map((project) => project.owner))].sort(), [projects]);

    useEffect(() => setVisibleCount(PAGE_SIZE), [query, owner, phase, sort]);

    const filtered = useMemo(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase();
        return projects
            .filter((project) => owner === 'all' || project.owner === owner)
            .filter((project) => {
                if (phase === 'operational') return project.currentPowerMw > 0;
                if (phase === 'pre_operational') return project.currentPowerMw <= 0;
                if (phase === 'forecast') return project.timeline.some((point) => point.date > asOf);
                return true;
            })
            .filter((project) => {
                if (!normalizedQuery) return true;
                const haystack = [
                    project.name,
                    project.owner,
                    project.country,
                    project.address,
                    project.project,
                    ...project.users.map((user) => user.name),
                    ...project.allChipTypes,
                ].join(' ').toLocaleLowerCase();
                return haystack.includes(normalizedQuery);
            })
            .sort((a, b) => {
                if (sort === 'power') return b.currentPowerMw - a.currentPowerMw || a.name.localeCompare(b.name);
                if (sort === 'recent') {
                    return (getLatestObserved(b, asOf)?.date || '').localeCompare(getLatestObserved(a, asOf)?.date || '') || a.name.localeCompare(b.name);
                }
                if (sort === 'name') return a.name.localeCompare(b.name);
                return b.currentH100Equivalent - a.currentH100Equivalent || a.name.localeCompare(b.name);
            });
    }, [asOf, owner, phase, projects, query, sort]);

    const visibleProjects = filtered.slice(0, visibleCount);

    return (
        <div className="ai-dev-dc-explorer">
            <div className="ai-dev-dc-toolbar">
                <label className="ai-dev-dc-search">
                    <span>{isZh ? '搜索数据中心' : 'Search data centers'}</span>
                    <input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={isZh ? '搜索站点、业主、国家或用户' : 'Search site, owner, country, or user'}
                    />
                </label>
                <label>
                    <span>{isZh ? '业主' : 'Owner'}</span>
                    <select value={owner} onChange={(event) => setOwner(event.target.value)}>
                        <option value="all">{isZh ? '全部业主' : 'All owners'}</option>
                        {owners.map((item) => <option value={item} key={item}>{item}</option>)}
                    </select>
                </label>
                <label>
                    <span>{isZh ? '建设状态' : 'Buildout status'}</span>
                    <select value={phase} onChange={(event) => setPhase(event.target.value)}>
                        <option value="all">{isZh ? '全部状态' : 'All states'}</option>
                        <option value="operational">{isZh ? '已有投运容量' : 'Has operational capacity'}</option>
                        <option value="pre_operational">{isZh ? '尚无投运容量' : 'No operational capacity'}</option>
                        <option value="forecast">{isZh ? '有未来估算' : 'Has future estimate'}</option>
                    </select>
                </label>
                <label>
                    <span>{isZh ? '排序' : 'Sort'}</span>
                    <select value={sort} onChange={(event) => setSort(event.target.value)}>
                        <option value="compute">{isZh ? '当前算力' : 'Current compute'}</option>
                        <option value="power">{isZh ? '当前 IT 功率' : 'Current IT power'}</option>
                        <option value="recent">{isZh ? '最近更新' : 'Latest observation'}</option>
                        <option value="name">{isZh ? '名称' : 'Name'}</option>
                    </select>
                </label>
            </div>

            <div className="ai-dev-dc-results">
                <div className="ai-dev-dc-results__meta">
                    <strong>{filtered.length}</strong> {isZh ? '个站点' : 'sites'}
                    <span>{isZh ? '点击任一站点展开建设时间线' : 'Select a site to expand its buildout timeline'}</span>
                </div>
                <div className="ai-dev-dc-list__head" aria-hidden="true">
                    <span>{isZh ? '站点 / 最近建设观察' : 'Site / latest buildout observation'}</span>
                    <span>{isZh ? '业主' : 'Owner'}</span>
                    <span>IT Power</span>
                    <span>H100-eq</span>
                </div>
                <div className="ai-dev-dc-list">
                    {visibleProjects.map((project) => {
                        const latest = getLatestObserved(project, asOf);
                        const hasProjection = project.timeline.some((point) => point.date > asOf);
                        const expanded = expandedProject === project.name;
                        return (
                            <div className={`ai-dev-dc-project ${expanded ? 'is-expanded' : ''}`.trim()} key={project.name}>
                                <button
                                    type="button"
                                    className="ai-dev-dc-project__toggle"
                                    aria-expanded={expanded}
                                    onClick={() => setExpandedProject(expanded ? null : project.name)}
                                >
                                    <span className="ai-dev-dc-project__identity">
                                        <strong>{project.name}</strong>
                                        <small>{project.address || project.country}</small>
                                        <em>{latest?.date || '—'} · {latest?.status || (isZh ? '暂无建设描述' : 'No buildout note')}</em>
                                    </span>
                                    <span className="ai-dev-dc-project__owner">{project.owner}</span>
                                    <span className="ai-dev-dc-project__metric"><strong>{formatPower(project.currentPowerMw)}</strong><small>{project.currentPowerMw > 0 ? (isZh ? '已有投运容量' : 'operational capacity') : (isZh ? '尚无投运容量' : 'no operational capacity')}</small></span>
                                    <span className="ai-dev-dc-project__metric"><strong>{formatCompute(project.currentH100Equivalent)}</strong><small>{hasProjection ? (isZh ? '含未来估算' : 'future estimate available') : (isZh ? '无未来节点' : 'no future point')}</small></span>
                                    <span className="ai-dev-dc-project__chevron" aria-hidden="true">{expanded ? '−' : '+'}</span>
                                </button>
                                {expanded ? <ProjectDetail project={project} asOf={asOf} isZh={isZh} /> : null}
                            </div>
                        );
                    })}
                </div>

                {!filtered.length ? <p className="ai-dev-dc-empty">{isZh ? '没有匹配的数据中心。' : 'No matching data centers.'}</p> : null}
                {visibleCount < filtered.length ? (
                    <button type="button" className="ai-dev-dc-more" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                        {isZh ? `继续显示（${Math.min(PAGE_SIZE, filtered.length - visibleCount)} 个）` : `Show ${Math.min(PAGE_SIZE, filtered.length - visibleCount)} more`}
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default DataCenterBuildoutExplorer;
